import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { COLORS } from '../constants/colors';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

interface Order {
  _id: string;
  userId: {
    name: string;
    email: string;
  };
  orderStatus: 'PAID' | 'PRINTING' | 'SHIPPED' | 'DELIVERED';
  status: 'pending' | 'completed' | 'failed';
  shippingAddress: {
    name: string;
    email: string;
    phone?: string;
    address: string;
    city: string;
    postalCode: string;
    country: string;
  };
  metadata: {
    orderDetails: {
      bookTitle: string;
      childName?: string;
      bindingType: string;
    };
    storyIds: string[];
    pricing: {
      total: number;
    };
  };
  totalAmount: number;
  createdAt: string;
  pdfUrl?: string;
}

const PageContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 32px 16px;
  width: 100%;
  box-sizing: border-box;
`;

const Header = styled.div`
  margin-bottom: 32px;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #212529;
  margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: #6c757d;
  margin: 0;
`;

const FilterSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  align-items: center;
`;

const FilterLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #495057;
  margin-right: 8px;
`;

const Select = styled.select`
  padding: 8px 12px;
  font-size: 0.938rem;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: ${COLORS.PRIMARY};
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: #6c757d;
  margin-bottom: 8px;
`;

const StatValue = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: #212529;
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const ScrollHint = styled.div`
  display: none;
  font-size: 0.75rem;
  color: #868e96;
  text-align: center;
  padding: 8px;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;

  @media (max-width: 768px) {
    display: block;
  }
`;

const Table = styled.table`
  width: 100%;
  min-width: 900px;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: #f8f9fa;
`;

const Th = styled.th`
  padding: 16px;
  text-align: left;
  font-size: 0.875rem;
  font-weight: 600;
  color: #495057;
  border-bottom: 2px solid #dee2e6;
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid #f1f3f5;
  
  &:hover {
    background: #f8f9fa;
  }
`;

const Td = styled.td`
  padding: 16px;
  font-size: 0.875rem;
  color: #495057;
`;

const StatusBadge = styled.span<{ $status: string }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.813rem;
  font-weight: 600;
  background: ${props => {
    switch (props.$status) {
      case 'PAID': return '#d1fae5';
      case 'PRINTING': return '#fed7aa';
      case 'SHIPPED': return '#bfdbfe';
      case 'DELIVERED': return '#c7d2fe';
      default: return '#e5e7eb';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'PAID': return '#065f46';
      case 'PRINTING': return '#92400e';
      case 'SHIPPED': return '#1e40af';
      case 'DELIVERED': return '#4338ca';
      default: return '#374151';
    }
  }};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'success' }>`
  padding: 6px 12px;
  font-size: 0.813rem;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
  white-space: nowrap;
  
  ${props => {
    if (props.$variant === 'success') {
      return `
        background: #22c55e;
        color: white;
        &:hover { background: #16a34a; }
      `;
    } else if (props.$variant === 'secondary') {
      return `
        background: #f1f3f5;
        color: #495057;
        &:hover { background: #e9ecef; }
      `;
    } else {
      return `
        background: ${COLORS.PRIMARY};
        color: white;
        &:hover { background: #5568d3; }
      `;
    }
  }}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadingContainer = styled.div`
  padding: 60px 20px;
  text-align: center;
`;

const EmptyState = styled.div`
  padding: 60px 20px;
  text-align: center;
  color: #6c757d;
`;

const Modal = styled.div<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 32px;
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #212529;
  margin: 0 0 20px 0;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #f1f3f5;
  
  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.span`
  font-size: 0.875rem;
  color: #6c757d;
  font-weight: 500;
`;

const DetailValue = styled.span`
  font-size: 0.875rem;
  color: #212529;
  font-weight: 600;
  text-align: right;
`;

// ============================================
// Publishable toggle styled components
// ============================================

const PublishableToggle = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 0.75rem;
  color: #6c757d;
  user-select: none;
  white-space: nowrap;
`;

const ToggleCheckbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: ${COLORS.PRIMARY};
`;

const ShareLink = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
`;

const ShareLinkText = styled.input`
  font-size: 0.7rem;
  color: #495057;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 3px 6px;
  width: 200px;
`;

const CopyButton = styled.button`
  background: none;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 3px 8px;
  font-size: 0.7rem;
  cursor: pointer;
  color: #495057;
  white-space: nowrap;
  
  &:hover {
    background: #f8f9fa;
  }
`;

const PublishedBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 8px;
  font-size: 0.7rem;
  font-weight: 600;
  background: #dbeafe;
  color: #1e40af;
`;

export const AdminOrders: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [generatingPdf, setGeneratingPdf] = useState<string | null>(null);
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Track which orders have "publishable" checked
  const [publishableOrders, setPublishableOrders] = useState<Set<string>>(new Set());

  // Track which orders have been published (bookId returned from backend)
  const [publishedBooks, setPublishedBooks] = useState<Record<string, string>>({});

  // Check admin access
  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      navigate('/login');
      return;
    }
    
    if (!user.isAdmin) {
      navigate('/');
      return;
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user?.isAdmin && !authLoading) {
      fetchOrders();
    }
  }, [statusFilter, user, authLoading]);

  if (authLoading) {
    return (
      <PageContainer>
        <LoadingContainer>Checking permissions...</LoadingContainer>
      </PageContainer>
    );
  }

  if (!user || !user.isAdmin) {
    return (
      <PageContainer>
        <LoadingContainer>Checking permissions...</LoadingContainer>
      </PageContainer>
    );
  }

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const url = statusFilter === 'all' 
        ? `${process.env.REACT_APP_API_URL}/books/orders`
        : `${process.env.REACT_APP_API_URL}/books/orders?status=${statusFilter}`;

      const response = await fetch(url, {
        headers: {
          // 'Authorization': `Bearer ${adminToken}`
        }
      });

      const result = await response.json();

      if (result.success) {
        setOrders(result.data.orders);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId: string, newStatus: string) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/books/orders/${orderId}/status`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ orderStatus: newStatus })
        }
      );

      const result = await response.json();

      if (result.success) {
        setOrders(prev => prev.map(order => 
          order._id === orderId 
            ? { ...order, orderStatus: newStatus as any }
            : order
        ));
        alert('Order status updated successfully');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update order status');
    }
  };

  const togglePublishable = (orderId: string) => {
    setPublishableOrders(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  };

  const handleGeneratePdf = async (orderId: string) => {
    setGeneratingPdf(orderId);
    const isPublishable = publishableOrders.has(orderId);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/books/orders/${orderId}/generate-pdf`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ publishable: isPublishable }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to generate PDF');
      }

      // Check if the response includes a bookId header (set by backend when publishable)
      const bookId = response.headers.get('X-Book-Id');
      if (bookId) {
        setPublishedBooks(prev => ({ ...prev, [orderId]: bookId }));
      }

      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `book-#${orderId.slice(-8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      if (isPublishable && bookId) {
        alert(`PDF generated & book published!\nShareable link:\nhttps://storify-now.com/cart?bookId=${bookId}`);
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF');
    } finally {
      setGeneratingPdf(null);
    }
  };

  const copyShareLink = (bookId: string) => {
    const link = `https://storify-now.com/cart?bookId=${bookId}`;
    navigator.clipboard.writeText(link);
    alert('Link copied!');
  };

  const stats = {
    total: orders.length,
    paid: orders.filter(o => o.orderStatus === 'PAID').length,
    printing: orders.filter(o => o.orderStatus === 'PRINTING').length,
    shipped: orders.filter(o => o.orderStatus === 'SHIPPED').length,
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingContainer>Loading orders...</LoadingContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header>
        <Title>Book Orders</Title>
        <Subtitle>Manage and track all physical book orders</Subtitle>
      </Header>

      <StatsGrid>
        <StatCard>
          <StatLabel>Total Orders</StatLabel>
          <StatValue>{stats.total}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>Paid</StatLabel>
          <StatValue>{stats.paid}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>In Production</StatLabel>
          <StatValue>{stats.printing}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>Shipped</StatLabel>
          <StatValue>{stats.shipped}</StatValue>
        </StatCard>
      </StatsGrid>

      <FilterSection>
        <div>
          <FilterLabel>Filter by Status:</FilterLabel>
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All Orders</option>
            <option value="PAID">Paid</option>
            <option value="PRINTING">Printing</option>
            <option value="SHIPPED">Shipped</option>
            <option value="DELIVERED">Delivered</option>
          </Select>
        </div>
      </FilterSection>

      {orders.length === 0 ? (
        <EmptyState>
          <p>No orders found</p>
        </EmptyState>
      ) : (
        <TableContainer>
          <ScrollHint>← Scroll to see all columns →</ScrollHint>
          <Table>
            <Thead>
              <Tr>
                <Th>Order ID</Th>
                <Th>Customer</Th>
                <Th>Book Title</Th>
                <Th>Stories</Th>
                <Th>Amount</Th>
                <Th>Status</Th>
                <Th>Date</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {orders.map(order => (
                <Tr key={order._id}>
                  <Td>#{order._id.slice(-8).toUpperCase()}</Td>
                  <Td>
                    <div>{order.shippingAddress?.name || order.userId?.name || 'N/A'}</div>
                    <div style={{ fontSize: '0.75rem', color: '#868e96' }}>
                      {order.shippingAddress?.email || order.userId?.email || 'N/A'}
                    </div>
                  </Td>
                  <Td>{order.metadata?.orderDetails?.bookTitle || 'Custom Book'}</Td>
                  <Td>{order.metadata?.storyIds?.length || 0}</Td>
                  <Td>€{(order.totalAmount / 100).toFixed(2)}</Td>
                  <Td>
                    <StatusBadge $status={order.orderStatus}>
                      {order.orderStatus}
                    </StatusBadge>
                  </Td>
                  <Td>{new Date(order.createdAt).toLocaleDateString()}</Td>
                  <Td>
                    <ActionButtons>
                      <Button
                        $variant="primary"
                        onClick={() => setSelectedOrder(order)}
                      >
                        View
                      </Button>
                      {order.pdfUrl ? (
                        <Button
                          $variant="success"
                          onClick={() => window.open(order.pdfUrl, '_blank')}
                        >
                          View PDF
                        </Button>
                      ) : (
                        <>
                          <Button
                            $variant="success"
                            onClick={() => handleGeneratePdf(order._id)}
                            disabled={generatingPdf === order._id}
                          >
                            {generatingPdf === order._id ? 'Generating...' : 'Generate PDF'}
                          </Button>
                          <PublishableToggle>
                            <ToggleCheckbox
                              type="checkbox"
                              checked={publishableOrders.has(order._id)}
                              onChange={() => togglePublishable(order._id)}
                            />
                            Publish
                          </PublishableToggle>
                        </>
                      )}
                      {publishedBooks[order._id] && (
                        <div>
                          <PublishedBadge>Published</PublishedBadge>
                          <ShareLink>
                            <ShareLinkText
                              readOnly
                              value={`https://storify-now.com/cart?bookId=${publishedBooks[order._id]}`}
                              onClick={(e) => (e.target as HTMLInputElement).select()}
                            />
                            <CopyButton onClick={() => copyShareLink(publishedBooks[order._id])}>
                              Copy
                            </CopyButton>
                          </ShareLink>
                        </div>
                      )}
                    </ActionButtons>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      {/* Order Details Modal */}
      <Modal $isOpen={selectedOrder !== null} onClick={() => setSelectedOrder(null)}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          {selectedOrder && (
            <>
              <ModalTitle>Order Details</ModalTitle>
              
              <DetailRow>
                <DetailLabel>Order ID</DetailLabel>
                <DetailValue>#{selectedOrder._id.slice(-8).toUpperCase()}</DetailValue>
              </DetailRow>
              
              <DetailRow>
                <DetailLabel>Book Title</DetailLabel>
                <DetailValue>{selectedOrder.metadata?.orderDetails?.bookTitle}</DetailValue>
              </DetailRow>
              
              {selectedOrder.metadata?.orderDetails?.childName && (
                <DetailRow>
                  <DetailLabel>Child's Name</DetailLabel>
                  <DetailValue>{selectedOrder.metadata.orderDetails.childName}</DetailValue>
                </DetailRow>
              )}
              
              <DetailRow>
                <DetailLabel>Binding Type</DetailLabel>
                <DetailValue>{selectedOrder.metadata?.orderDetails?.bindingType}</DetailValue>
              </DetailRow>
              
              <DetailRow>
                <DetailLabel>Number of Stories</DetailLabel>
                <DetailValue>{selectedOrder.metadata?.storyIds?.length}</DetailValue>
              </DetailRow>
              
              <DetailRow>
                <DetailLabel>Total Amount</DetailLabel>
                <DetailValue>€{(selectedOrder.totalAmount / 100).toFixed(2)}</DetailValue>
              </DetailRow>
              
              <ModalTitle style={{ marginTop: '24px' }}>Shipping Address</ModalTitle>
              
              <DetailRow>
                <DetailLabel>Name</DetailLabel>
                <DetailValue>{selectedOrder.shippingAddress?.name}</DetailValue>
              </DetailRow>
              
              <DetailRow>
                <DetailLabel>Email</DetailLabel>
                <DetailValue>{selectedOrder.shippingAddress?.email}</DetailValue>
              </DetailRow>
              
              {selectedOrder.shippingAddress?.phone && (
                <DetailRow>
                  <DetailLabel>Phone</DetailLabel>
                  <DetailValue>{selectedOrder.shippingAddress.phone}</DetailValue>
                </DetailRow>
              )}
              
              <DetailRow>
                <DetailLabel>Address</DetailLabel>
                <DetailValue>{selectedOrder.shippingAddress?.address}</DetailValue>
              </DetailRow>
              
              <DetailRow>
                <DetailLabel>City</DetailLabel>
                <DetailValue>{selectedOrder.shippingAddress?.city}</DetailValue>
              </DetailRow>
              
              <DetailRow>
                <DetailLabel>Postal Code</DetailLabel>
                <DetailValue>{selectedOrder.shippingAddress?.postalCode}</DetailValue>
              </DetailRow>
              
              <DetailRow>
                <DetailLabel>Country</DetailLabel>
                <DetailValue>{selectedOrder.shippingAddress?.country}</DetailValue>
              </DetailRow>
              
              <ModalTitle style={{ marginTop: '24px' }}>Update Status</ModalTitle>
              
              <Select 
                value={selectedOrder.orderStatus}
                onChange={(e) => {
                  handleStatusChange(selectedOrder._id, e.target.value);
                  setSelectedOrder(null);
                }}
                style={{ width: '100%', marginBottom: '16px' }}
              >
                <option value="PAID">Paid</option>
                <option value="PRINTING">Printing</option>
                <option value="SHIPPED">Shipped</option>
                <option value="DELIVERED">Delivered</option>
              </Select>
              
              <Button 
                $variant="secondary" 
                onClick={() => setSelectedOrder(null)}
                style={{ width: '100%' }}
              >
                Close
              </Button>
            </>
          )}
        </ModalContent>
      </Modal>
    </PageContainer>
  );
};